/**
 * Sprint 4 equipment additions — 2026-05-19.
 *
 * Three new SKUs verified from manufacturer datasheets in
 * ~/Desktop/katalogi/ (the 4th file in that batch was the Dahua
 * IPC-HFW3449T1-AS-PV-PRO camera, updated separately in
 * tools/camera-additions.json since it already existed there with
 * placeholder values).
 *
 * Sources:
 *  - NVR5216-EI2_S0_datasheet_20260115.pdf  (Dahua, 2026-01-15)
 *  - RG-EST100-E_Datasheet_Official_2025-12-04.pdf  (Ruijie, 2025-12-04)
 *  - U-POE-at datasheet (Ubiquiti, file POE-48G-30WN.pdf misnamed but
 *    contents are the single-port U-POE-at adapter, 2022 datasheet)
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const SPRINT4_EQUIPMENT_ADDITIONS: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // Dahua NVR5216-EI2 — 16-ch WizSense AI NVR, 1U, 2 SATA, no built-in PoE.
  // Different SKU from existing NVR5216-16P-EI (which has 16-port PoE
  // built-in). EI2 is the non-PoE chassis — paired with external PoE
  // switch. 2026 hardware revision with bigger face DB (20k plates).
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-nvr5216-ei2',
    manufacturer: 'Dahua',
    model: 'NVR5216-EI2',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense AI NVR — 1U, 2 SATA (20 TB each), AcuPick, face DB 20k, no built-in PoE',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Network-Video-Recorders/WizSense-Series/5-Series/4K/NVR5216-EI2',
    specs: {
      channels: 16,
      inputBandwidthMbps: 448,  // AI disabled. AI enabled drops to 200 Mbps.
      outputBandwidthMbps: 448,
      maxResolution: '32 MP',   // decoding: 1ch 32MP / 2ch 24MP / 16ch 4MP@30fps
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,            // EI2 variant has no built-in PoE — pair with switch
      codecs: ['H.265', 'H.265+', 'H.264'],
      onvif: true,              // ONVIF 25.06 Profile S/T/G/M
      formFactor: 'rack-1U',
      networkPorts: '2× RJ45 (gigabit)',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ubiquiti U-POE-at — single-port 802.3at PoE+ injector adapter.
  // Strictly an "injector" rather than a switch, but functionally provides
  // PoE on a non-PoE LAN drop, so it slots into the poe-switch category
  // with totalPorts=1 for catalog discoverability. Common pairing: power
  // a single UniFi Protect camera from a non-PoE switch.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ubiquiti-u-poe-at',
    // Filed as a one-port switch before the injector category existed; the
    // injector entry lives in pole-distribution-catalog.ts. Hidden, not
    // removed, so projects placed from this id keep resolving.
    hidden: true,
    manufacturer: 'Ubiquiti',
    model: 'U-POE-at',
    category: 'poe-switch',
    tagline: 'Single-port 802.3at PoE+ injector — 30 W, gigabit pass-through, brick form factor',
    verified: true,
    datasheetUrl: 'https://store.ui.com/us/en/products/u-poe-at',
    specs: {
      totalPorts: 1,
      poePorts: 1,
      poeBudgetW: 30,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
      uplinks: '1× RJ45 gigabit (LAN side)',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ruijie RG-EST100-E — 2.4 GHz wireless bridge, 500 m point-to-point.
  // Cheaper alternative to Ubiquiti NanoStation for short-range CCTV
  // installs. Handles ~10× 3MP cameras at 100 m or 5× 3MP at 500 m. Pairs
  // run factory-free (zero-config alignment).
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ruijie-rg-est100-e',
    manufacturer: 'Ruijie',
    model: 'RG-EST100-E',
    category: 'radio-antenna',
    tagline: '2.4 GHz wireless bridge — 500 m PtP for CCTV, IP55, factory-free pairing, 10×3MP at 100 m',
    verified: true,
    datasheetUrl: 'https://www.ruijienetworks.com/products/access-points/RG-EST100-E',
    specs: {
      band: '2.4 GHz',
      rangeM: 500,
      gainDbi: 8,
      beamWidthDeg: 70,
      standard: '802.11n',
      mimo: '2×2',
      poePassthrough: false,
      operatingTempC: '-30 to +60',
      ipRating: 'IP55',
    } as any, // RadioAntennaSpec spread — operatingTempC + ipRating may not be
              // strictly typed; the data displays on the picker either way.
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ruijie RG-EST310 V2 — 5 GHz AC dual-stream wireless bridge, 1 km PtP.
  // The 5 GHz, 802.11ac step up from the RG-EST100-E (2.4 GHz): 866 Mbps,
  // 2×2 MIMO, 10 dBi built-in directional (60°×30°), PtP or PtMP to 5
  // clients, factory-paired with auto-rebridge on power restore. Built for
  // elevator / no-cable CCTV. Passive PoE (24 V) or 12 V DC, IP54.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ruijie-rg-est310-v2',
    manufacturer: 'Ruijie',
    model: 'RG-EST310 V2',
    category: 'radio-antenna',
    tagline: '5 GHz AC wireless bridge — 866 Mbps, 1 km PtP/PtMP (5 clients), 10 dBi, IP54, factory-paired',
    verified: true,
    datasheetUrl: 'https://www.ruijienetworks.com/products/access-points/RG-EST310-V2',
    specs: {
      band: '5 GHz',
      rangeM: 1000,
      gainDbi: 10,
      beamWidthDeg: 60,
      throughputMbps: 866,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive (or 12 V DC)',
      operatingTempC: '-30 to +55',
      ipRating: 'IP54',
      powerConsumptionW: 7,
    } as any, // RadioAntennaSpec spread — matches the RG-EST100-E entry style.
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ruijie RG-AirMetro460G — 5 GHz Wi-Fi 5 long-range CPE, up to 15 km PtP.
  // The high-end self-contained bridge: 23 dBi built-in directional dish
  // (9° beam), 866 Mbps, 2×2 MIMO, IP65, 220 km/h wind survivability,
  // ±24 kV ESD. Cloud-managed, 30-sec pairing. For backbone CCTV hops
  // between sites where trenching fibre isn't viable. Passive 24 V PoE.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ruijie-rg-airmetro460g',
    manufacturer: 'Ruijie',
    model: 'RG-AirMetro460G',
    category: 'radio-antenna',
    tagline: '5 GHz AC long-range CPE bridge — 866 Mbps, up to 15 km PtP, 23 dBi/9° dish, IP65',
    verified: true,
    datasheetUrl: 'https://www.ruijienetworks.com/products/RG-AirMetro460G',
    specs: {
      band: '5 GHz',
      rangeM: 15000,
      gainDbi: 23,
      beamWidthDeg: 9,
      throughputMbps: 866,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive PoE',
      operatingTempC: '-40 to +70',
      ipRating: 'IP65',
      powerConsumptionW: 12,
    } as any, // RadioAntennaSpec spread — matches the RG-EST100-E entry style.
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ruijie RG-AirMetro550G-B — 5 GHz PTMP base station (1:32, up to 10 km).
  // Hub for point-to-multipoint CCTV backhaul: one base fans out to up to
  // 32 CPE clients. 866 Mbps, 2×2 MIMO, dual GE ports, 24 V passive PoE or
  // 12 V DC (solar-friendly), IP55, 6 kV surge. NOTE: the built-in antenna
  // is only a 3 dBi 2.4 GHz omni for management — 5 GHz DATA needs an
  // EXTERNAL directional antenna. Range scales with the chosen antenna:
  // RG-ANT13-360 (13 dBi) ≈ 2 km, RG-ANT16S-120 (16 dBi) ≈ 5 km,
  // RG-ANT20S-90 (20 dBi) ≈ 10 km. Specs below reflect the 10 km / 20 dBi
  // sector pairing so range and gain stay internally consistent.
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ruijie-rg-airmetro550g-b',
    manufacturer: 'Ruijie',
    model: 'RG-AirMetro550G-B',
    category: 'radio-antenna',
    tagline: '5 GHz PTMP base station — 1:32 clients, up to 10 km, 866 Mbps (needs external sector antenna)',
    verified: true,
    datasheetUrl: 'https://www.ruijienetworks.com/products/RG-AirMetro550G-B',
    specs: {
      band: '5 GHz',
      rangeM: 10000,
      gainDbi: 20,
      beamWidthDeg: 90,
      throughputMbps: 866,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive PoE (or 12 V DC)',
      operatingTempC: '-40 to +80',
      ipRating: 'IP55',
      powerConsumptionW: 12,
    } as any, // RadioAntennaSpec spread — matches the RG-EST100-E entry style.
  },

  // ─────────────────────────────────────────────────────────────────────
  // Ruijie RG-ANT13-360 — 360° omni accessory antenna (5 GHz, 13 dBi) for
  // the RG-AirMetro base stations. NOT a standalone bridge: it has no radio
  // of its own. Modelled here in radio-antenna so it can be placed on a
  // mast and quoted in the BOM; rangeM is the typical AirMetro link reach
  // with this omni (≈ 2 km, per the 550G-B datasheet pairing table).
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'ruijie-rg-ant13-360',
    manufacturer: 'Ruijie',
    model: 'RG-ANT13-360',
    category: 'radio-antenna',
    tagline: '360° omni accessory antenna (5 GHz, 13 dBi) for RG-AirMetro base stations — ≈ 2 km links',
    verified: true,
    datasheetUrl: 'https://www.ruijienetworks.com/products/RG-ANT13-360',
    specs: {
      band: '5 GHz',
      rangeM: 2000,
      gainDbi: 13,
      beamWidthDeg: 360,
      mimo: '2×2',
      operatingTempC: '-40 to +80',
      ipRating: 'IP55',
    } as any, // RadioAntennaSpec spread — accessory antenna (no built-in radio).
  },
];
